import { createTheme } from '@mui/material';

/** The one MUI theme of the PC admin and the operations site. */
export const theme = createTheme({
  palette: {
    primary: { main: '#17665f', dark: '#104d47', light: '#e8f3f0' },
    secondary: { main: '#b98744' },
    background: { default: '#f5f7f8', paper: '#ffffff' },
    text: { primary: '#1d3138', secondary: '#6c7c83' },
    divider: '#e7edef',
  },
  typography: {
    fontFamily:
      'Inter, -apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", sans-serif',
    button: { textTransform: 'none', fontWeight: 600 },
    h4: { fontWeight: 700, letterSpacing: '-0.7px' },
    h6: { fontWeight: 650 },
  },
  // MUI's own 4px; every corner on the platform takes it, chips and buttons included.
  shape: { borderRadius: 4 },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: { root: { border: '1px solid #e7edef' } },
    },
    // Alert and the snackbar are Papers too, but read as MUI's borderless tinted bars.
    MuiAlert: { styleOverrides: { root: { border: 0 } } },
    // A drawer sits on its shadow; a card border would push its header 1px below the top bar.
    MuiDrawer: { styleOverrides: { paper: { border: 0 } } },
    MuiSnackbarContent: { styleOverrides: { root: { border: 0 } } },
    MuiTableCell: {
      styleOverrides: {
        // Header text keeps MUI's own size, colour and weight; only the band is tinted.
        head: { background: '#f8fafb' },
        // Padding is MUI's own, so size="small" really is compact for one-line rows.
        root: { borderBottom: '1px solid #edf1f2' },
      },
    },
    // MUI rounds chips into pills; here they square off like every other control.
    MuiChip: {
      styleOverrides: {
        root: ({ theme }) => ({ fontWeight: 550, borderRadius: theme.shape.borderRadius }),
      },
    },
    MuiTextField: { defaultProps: { size: 'small', fullWidth: true } },
    // Fields read at 14px like the rest of the page, not MUI's 16px body text; a small one is
    // then 36px high, level with a medium button beside it.
    MuiInputBase: { styleOverrides: { root: { fontSize: 14 } } },
    // Autocomplete and multiline boxes pad their own root and keep MUI's spacing.
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&.MuiInputBase-sizeSmall .MuiOutlinedInput-input:not(.MuiAutocomplete-input):not(.MuiInputBase-inputMultiline)':
            { paddingTop: 8, paddingBottom: 8 },
        },
      },
    },
    // A floating label at the same size, centred in the 36px box until it floats up.
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: 14,
          '&.MuiInputLabel-sizeSmall:not(.MuiInputLabel-shrink)': {
            transform: 'translate(14px, 8px) scale(1)',
          },
        },
      },
    },
    // What a field drops down matches the field.
    MuiMenuItem: { styleOverrides: { root: { fontSize: 14 } } },
    MuiAutocomplete: {
      styleOverrides: {
        option: { fontSize: 14 },
        // 36px like a plain field beside it, not MUI's 37.
        root: {
          '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall .MuiAutocomplete-input': {
            paddingTop: 2,
            paddingBottom: 2,
          },
          // With values as chips too: one row of chips is the same 36px as a plain field.
          '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall:has(.MuiAutocomplete-tag)': {
            paddingTop: 4,
            paddingBottom: 4,
          },
        },
      },
    },
    MuiDialog: { defaultProps: { fullWidth: true, maxWidth: 'sm' } },
  },
});
